/**
 * 编辑器 iframe → Vue 平台 API 桥接
 * 模版 / 素材 / 字体与平台侧模块共用同一套 scj 接口
 */

import {
  resolveRemoteTemplateForUse,
  syncTemplateLibrary,
  importTemplateWithLayerStorageRemote,
} from '@/services/templateStore';
import { syncMaterialLibrary } from '@/services/materialStore';
import {
  syncQueryMaterialLibrary,
  syncQueryTemplateLibrary,
} from '@/services/queryLibraryStore';
import { buildEditorSyncPayload } from '@/utils/editorBridge';
import { runAiImageEditFromPageExports } from '@/services/aiImageEditStore';
import { checkDraftExportMaxCount, recordDraftExport } from '@/services/draftStore';

function dataUrlToFile(dataUrl, fileName, mimeType) {
  const raw = String(dataUrl || '');
  const comma = raw.indexOf(',');
  const header = comma >= 0 ? raw.slice(0, comma) : '';
  const base64 = comma >= 0 ? raw.slice(comma + 1) : raw;
  const mime = mimeType || header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], fileName || 'upload.bin', { type: mime });
}

function resolveStoreId(storeId, session) {
  if (storeId && storeId !== 'all') return storeId;
  if (session.storeId && session.storeId !== 'all') return session.storeId;
  if (session.departmentId != null && Number(session.departmentId) > 0) {
    return `s${session.departmentId}`;
  }
  return 's1';
}

function buildEditorApiContext(ctx, storeId) {
  const { platform, session, user } = ctx;
  const roleUser = user?.value || user || {};
  const resolvedStoreId = resolveStoreId(storeId, session);

  // 编辑器内切换门店后，session.departmentId 仍是旧值，
  // 会导致 resolveDepartmentFromStore 的 sessionDept 分支优先返回旧 departmentId，
  // 使接口入参和渲染数据始终不变。检测到 storeId 变化时清除过期部门上下文。
  const storeChanged = session.storeId
    && session.storeId !== 'all'
    && resolvedStoreId !== 'all'
    && resolvedStoreId !== session.storeId;
  const departmentId = storeChanged ? null : session.departmentId;
  const departmentName = storeChanged ? '' : session.departmentName;

  return {
    storeId: resolvedStoreId,
    user: roleUser,
    stores: platform.stores || [],
    departmentId,
    departmentName,
  };
}

function buildSyncResult(ctx) {
  const {
    platform,
    session,
    platformFonts,
    USE_REMOTE_MATERIALS,
    USE_REMOTE_TEMPLATES,
    USE_FONT_API,
    fontApiAvailable,
  } = ctx;
  const mats = (platform.queryMaterials?.length || USE_REMOTE_MATERIALS)
    ? (platform.queryMaterials?.length ? platform.queryMaterials : platform.materials)
    : platform.materials;
  const matFolders = (platform.queryMaterialFolders && Object.keys(platform.queryMaterialFolders).length)
    ? platform.queryMaterialFolders
    : (platform.materialFolders || {});
  const tpls = (platform.queryTemplates?.length || USE_REMOTE_TEMPLATES)
    ? (platform.queryTemplates?.length ? platform.queryTemplates : platform.templates)
    : platform.templates;
  const tplFolders = (platform.queryTemplateFolders && Object.keys(platform.queryTemplateFolders).length)
    ? platform.queryTemplateFolders
    : (platform.templateFolders || {});

  return buildEditorSyncPayload({
    materials: mats || [],
    materialFolders: matFolders || {},
    platformFonts: platformFonts?.value || [],
    useRemoteMaterials: USE_REMOTE_MATERIALS,
    deletedDefaultMaterialIds: platform.deletedDefaultMaterialIds || [],
    templates: tpls || [],
    templateFolders: tplFolders || {},
    useRemoteTemplates: USE_REMOTE_TEMPLATES,
    useRemoteFonts: USE_FONT_API && fontApiAvailable?.value,
    stores: platform.stores || [],
    organizationTree: platform.organizationTree || null,
    currentStoreId: session?.storeId || 'all',
  });
}

export async function executeEditorApiAction(action, payload = {}, ctx = {}) {
  const {
    platform,
    session,
    user,
    platformFonts,
    uploadMaterial,
    checkPlatformFontMissing,
    loadFonts,
    pushEditorSync,
    showToast,
    savePendingTemplate,
    USE_REMOTE_MATERIALS,
    USE_REMOTE_TEMPLATES,
    USE_FONT_API,
    fontApiAvailable,
  } = ctx;

  const storeId = resolveStoreId(payload.storeId, session);
  const roleUser = user?.value || user || {};
  const apiContext = buildEditorApiContext(ctx, storeId);

  switch (action) {
    case 'templates.resolve': {
      const templateId = payload.templateId;
      if (!templateId) throw new Error('缺少 templateId');
      const existing = [
        ...(platform.queryTemplates || []),
        ...(platform.templates || []),
      ].find(item => String(item.id) === String(templateId));
      if (!USE_REMOTE_TEMPLATES || String(templateId).startsWith('t_')) {
        return {
          template: existing || { id: templateId },
          ...buildSyncResult(ctx),
        };
      }
      const resolvedStoreId = resolveStoreId(payload.storeId || existing?.storeId, session);
      const working = await resolveRemoteTemplateForUse({
        storeId: resolvedStoreId,
        user: roleUser,
        templateId,
        existing: existing || {},
        stores: platform.stores,
        departmentId: payload.departmentId ?? session.departmentId,
        departmentName: payload.departmentName ?? session.departmentName,
        recoverFromPdf: true,
        onRecoverStart: () => showToast?.('云端模版缺少分层数据，正在从原 PDF 自动恢复…'),
        onRecoverSuccess: () => showToast?.('模版分层已恢复，可以继续设计', 'success'),
        persistRecovered: savePendingTemplate,
      });
      platform.templates = [
        working,
        ...(platform.templates || []).filter(item => String(item.id) !== String(working.id)),
      ];
      if ((platform.queryTemplates || []).some(item => String(item.id) === String(templateId))) {
        platform.queryTemplates = [
          working,
          ...(platform.queryTemplates || []).filter(item => String(item.id) !== String(working.id)),
        ];
      }
      pushEditorSync?.();
      return {
        template: {
          id: working.id,
          name: working.name,
          thumb: working.thumb,
          layerJson: working.layerJson,
          storeId: working.storeId,
          originFileUrl: working.originFileUrl,
          folderId: working.folderId,
          type: working.type,
          tags: working.tags,
        },
        ...buildSyncResult(ctx),
      };
    }
    case 'templates.import': {
      if (!USE_REMOTE_TEMPLATES) {
        throw new Error('远程模版库未启用');
      }
      const outcome = await importTemplateWithLayerStorageRemote(
        storeId,
        roleUser,
        payload,
        platform.stores,
        {
          requireLayerJson: payload.requireLayerJson !== false,
          autoUseTemplate: payload.autoUseTemplate === true,
          ...apiContext,
        },
      );
      platform.templates = [
        outcome.template,
        ...(platform.templates || []).filter(item => String(item.id) !== String(outcome.template.id)),
      ];
      pushEditorSync?.();
      return outcome;
    }
    case 'templates.sync': {
      if (USE_REMOTE_TEMPLATES) {
        await syncTemplateLibrary(platform, storeId, roleUser, apiContext);
        try {
          await syncQueryTemplateLibrary(platform, storeId, roleUser, apiContext);
        } catch {}
        pushEditorSync?.();
      }
      return buildSyncResult(ctx);
    }
    case 'materials.sync': {
      if (USE_REMOTE_MATERIALS) {
        await syncMaterialLibrary(platform, storeId, roleUser, apiContext);
        try {
          await syncQueryMaterialLibrary(platform, storeId, roleUser, apiContext);
        } catch {}
        pushEditorSync?.();
      }
      return buildSyncResult(ctx);
    }
    case 'materials.upload': {
      if (!payload?.dataUrl) throw new Error('缺少素材文件数据');
      const file = dataUrlToFile(payload.dataUrl, payload.fileName, payload.mimeType);
      const previousStoreId = session.storeId;
      if (payload.storeId && payload.storeId !== 'all') {
        session.storeId = payload.storeId;
      }
      try {
        const material = await uploadMaterial(file, {
          name: payload.name,
          category: payload.category,
          folderId: payload.folderId,
        });
        if (material?.error) throw new Error(material.error);
        if (!material) throw new Error('素材上传失败');
        return {
          material,
          ...buildSyncResult(ctx),
        };
      } finally {
        session.storeId = previousStoreId;
      }
    }
    case 'fonts.sync': {
      if (USE_FONT_API && fontApiAvailable.value) {
        await loadFonts({ force: true });
        pushEditorSync?.();
      }
      return buildSyncResult(ctx);
    }
    case 'fonts.checkMissing': {
      const missing = await checkPlatformFontMissing(payload.families || payload.fontFamilies || []);
      return { missing };
    }
    case 'design.autosave': {
      if (!ctx.USE_REMOTE_DESIGN_CENTER || typeof ctx.autosaveDesignFromEditor !== 'function') {
        return { saved: false, localOnly: true };
      }
      const outcome = await ctx.autosaveDesignFromEditor(payload);
      if (outcome?.saved) return outcome;
      return { saved: false, localOnly: true, ...outcome };
    }
    case 'design.save': {
      if (!ctx.USE_REMOTE_DESIGN_CENTER || typeof ctx.saveDesignFromEditor !== 'function') {
        return { saved: false, localOnly: true };
      }
      const saved = await ctx.saveDesignFromEditor(payload);
      if (saved === false) throw new Error('保存设计失败');
      if (typeof saved === 'object' && saved?.localOnly) {
        if (saved.masterTemplate) {
          const id = saved.id != null ? saved.id : payload.templateId || payload.fromTemplate;
          return { saved: true, localOnly: true, id, masterTemplate: true };
        }
        return { saved: false, localOnly: true };
      }
      const id = typeof saved === 'object' && saved?.id != null ? saved.id : payload.id;
      const result = { saved: true, id };
      if (saved?.masterTemplate) result.masterTemplate = true;
      return result;
    }
    case 'aiImage.edit': {
      const fromDataUrl = String(payload?.fromDataUrl ?? '').trim();
      const toDataUrl = String(payload?.toDataUrl ?? '').trim();
      if (!fromDataUrl) throw new Error('缺少第二页导出图片');
      if (!toDataUrl) throw new Error('缺少第一页导出图片');
      const outcome = await runAiImageEditFromPageExports({
        fromDataUrl,
        toDataUrl,
        businessId: payload.businessId ?? storeId,
      });
      const imageUrl = String(outcome?.imageUrl ?? '').trim();
      if (!imageUrl) throw new Error('AI 渲染未返回图片');
      return { imageUrl };
    }
    case 'draft.checkExportQuota': {
      try {
        return await checkDraftExportMaxCount();
      } catch (error) {
        if (import.meta.env.DEV) console.warn('[draft-export] 导出上限校验失败，已放行导出', error);
        return { exceeded: false, checkFailed: true, dailyLimit: null, todayCount: null };
      }
    }
    case 'draft.recordExport': {
      const recorded = await recordDraftExport(payload);
      return { recorded: recorded === true };
    }
    default:
      throw new Error(`未知编辑器 API 动作: ${action}`);
  }
}
